import { ReactElement, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/shallow';

import { Box } from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon
} from '@mui/icons-material';

import { useMSALPayload } from '@initia-digital/auth-client-utils';
import { Alert, IconButton, InitiaSystemDrawer, InitiaTopAppBar } from '@initia-digital/component-library';
import { baseDefinedAndNotNullNarrowing, useOptionalProp } from '@initia-digital/ts-utils';

import { fetchMsalIdentity } from '../api/query';
import { CONFIG } from '../config/const';
import { useWorkAndSafetyStore } from '../store';
import { useUpdateServiceWorker } from '../components/serviceWorkerUpdates';
const {
  DEFAULT_PAGE_CONFIG: PAGE_CONFIG,
  INITIA_SYSTEMS_APP_BAR_AND_PANEL_VISIBILITY_FILTER
} = CONFIG;

export interface IPageStyles {
  bgColor?: string
}

export interface IPageProps {
  children: ReactElement
}


export const BasicAppBarPage = ({ children, bgColor }: IPageProps & IPageStyles) => {
  // --------------------------------------------------------------
  // ZUSTAND GLOBAL STORE
  const {
    isSideDrawerOpen,
    setIsSideDrawerOpen
  } = useWorkAndSafetyStore(useShallow(state => ({
    isSideDrawerOpen: state.isSideDrawerOpen,
    setIsSideDrawerOpen: state.setIsSideDrawerOpen
  })));
  
  const { isUpdateAvailable, triggerUpdate } = useUpdateServiceWorker();

  const setBgColor = useOptionalProp(bgColor, 'white');

  // ROUTER
  const navigate = useNavigate();

  //  AUTHENTICATION PAYLOAD
  // -----------------------
  const authDetail = useMSALPayload();

  // -----------------------
  //    USER IDENTITY 
  // -----------------------
  const { data: identity, isLoading: isIdentityLoading } = useQuery({
    queryKey: ['identity'],
    queryFn: () => fetchMsalIdentity(authDetail),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 12, // 12 hours
    retry: 3
  });

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <InitiaTopAppBar
        appName={PAGE_CONFIG.appName}
        appVersion={PAGE_CONFIG.appVersion}
        avatarButtonFilterFn={INITIA_SYSTEMS_APP_BAR_AND_PANEL_VISIBILITY_FILTER}
        userIconSrc={!isIdentityLoading && baseDefinedAndNotNullNarrowing(identity) && baseDefinedAndNotNullNarrowing(identity.profileImage) ? identity.profileImage : undefined}
        userLabel={!isIdentityLoading && baseDefinedAndNotNullNarrowing(identity) && baseDefinedAndNotNullNarrowing(identity.azureAd.user?.displayName) ? identity.azureAd.user?.displayName : undefined}
        showHomeImage={window.location.pathname !== '/'}
        homeImageOverride={
          <IconButton
            color="black"
            size='large'
          >
            <HomeIcon sx={{ height: '30px', width: '30px' }} />
          </IconButton>
        }
        homeImageTooltipLabel='Home'
        homeImageHandleClick={() => navigate('/')}
        menuIconButton={
            <Box
              border={"1px solid transparent"}
              borderRadius="5px"
            >
              <IconButton

                color="black"
                size='large'
                onClick={() => setIsSideDrawerOpen(true)}
              >
                <MenuIcon sx={{ height: '30px', width: '30px' }} />
              </IconButton>
            </Box>
        }
      />
      <InitiaSystemDrawer
        open={isSideDrawerOpen}
        handleOpen={() => setIsSideDrawerOpen(true)}
        handleClose={() => setIsSideDrawerOpen(false)}
        systemFilterFn={INITIA_SYSTEMS_APP_BAR_AND_PANEL_VISIBILITY_FILTER}
        handleRedirect={() => { throw new Error('Redirect handler not implemented') }}
      />
      < Box flexGrow={1} display="flex" flexDirection="column" paddingTop="10px" paddingBottom="50px" bgcolor={setBgColor} sx={{ transition: 'background-color 0.5s ease' }}>
      {
            isUpdateAvailable &&
            <Box my="10px">
                <Alert
                    title={`A new version is available`} severity="warning"
                    suggestedActionsLabel={`Recommended action`}
                    suggestedActions={[
                        {
                            children: 'UPDATE',
                            customSize: {
                                minWidth: "60px",
                                fontSize: "0.7rem",
                                padding: "4px 8px"
                            },
                            onClick: () => { triggerUpdate(); }
                        }
                    ]}
                >
                    {`Please update to ensure you have the latest features`}
                </Alert>
            </Box>
        }
        {
          cloneElement(children)
        }
      </Box >
    </Box >
  );
} 